import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, X, StickyNote, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  amber: "bg-amber-500/10 border-amber-500/30 text-amber-100",
  rose: "bg-rose-500/10 border-rose-500/30 text-rose-100",
  emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-100",
  sky: "bg-sky-500/10 border-sky-500/30 text-sky-100",
  violet: "bg-violet-500/10 border-violet-500/30 text-violet-100",
  orange: "bg-orange-500/10 border-orange-500/30 text-orange-100",
};

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", color: "amber" });
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/notes`); setNotes(r.data.notes || []); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title: "", body: "", color: "amber" }); setOpen(true); };
  const openEdit = (n) => { setEditing(n.id); setForm({ title: n.title, body: n.body || "", color: n.color || "amber" }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { setError("Titlul este obligatoriu"); return; }
    try {
      if (editing) await axios.put(`${API}/notes/${editing}`, form);
      else await axios.post(`${API}/notes`, form);
      setOpen(false); setError(""); load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Ștergi această notiță?")) return;
    await axios.delete(`${API}/notes/${id}`);
    load();
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="notes-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Notițe</h1>
          <p className="text-sm text-zinc-500">Idei, observații și informații rapide</p>
        </div>
        <button data-testid="new-note-btn" onClick={openNew}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-orange-500 to-rose-500 px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-orange-500/20 hover:from-orange-400 hover:to-rose-400">
          <Plus className="h-4 w-4" /> Notiță nouă
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          <AlertCircle className="mt-0.5 h-4 w-4" /><div className="flex-1">{error}</div>
          <button onClick={() => setError("")}><X className="h-4 w-4" /></button>
        </div>
      )}

      {loading ? (
        <p className="text-zinc-500">Se încarcă...</p>
      ) : notes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center">
          <StickyNote className="mx-auto mb-3 h-10 w-10 text-zinc-700" />
          <p className="text-zinc-400">Nicio notiță încă. Apasă „Notiță nouă" pentru a începe.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="notes-grid">
          {notes.map((n) => {
            const cls = COLORS[n.color] || COLORS.amber;
            return (
              <div key={n.id} data-testid={`note-${n.id}`} onClick={() => openEdit(n)}
                className={`group relative cursor-pointer rounded-2xl border p-4 transition hover:scale-[1.02] ${cls}`}>
                <h3 className="pr-6 text-sm font-semibold">{n.title}</h3>
                <p className="mt-2 line-clamp-6 whitespace-pre-wrap text-xs opacity-90">{n.body}</p>
                <p className="mt-3 text-[10px] uppercase tracking-wider opacity-60">{new Date(n.updated_at).toLocaleString("ro-RO")}</p>
                <button onClick={(e) => remove(n.id, e)} className="absolute right-2 top-2 rounded p-1 opacity-0 transition group-hover:opacity-100 hover:bg-black/30">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-zinc-100">{editing ? "Editează notița" : "Notiță nouă"}</h3>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-1 min-h-0 flex-col gap-3">
              <input data-testid="note-title-input" placeholder="Titlu *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
              <textarea placeholder="Conținut..." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="min-h-[40vh] w-full flex-1 resize-none rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm leading-relaxed text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
              <div>
                <p className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Culoare</p>
                <div className="flex gap-2">
                  {Object.keys(COLORS).map((c) => (
                    <button key={c} onClick={() => setForm({ ...form, color: c })}
                      className={`h-8 w-8 rounded-full border-2 ${COLORS[c]} ${form.color === c ? "ring-2 ring-white/40" : "border-transparent"}`} />
                  ))}
                </div>
              </div>
              <button data-testid="note-save-btn" onClick={save} className="w-full rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2.5 text-sm font-medium text-white hover:from-orange-400 hover:to-rose-400">
                {editing ? "Salvează" : "Adaugă notiță"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
